/**
 * GameSampleLoader - On-demand loading of game sample data
 *
 * Caches game sample files and provides resolution of symbolic sample references.
 */

import type { Project, Sample, InlineSample, SymbolicSample, SymbolicSampleRef } from './types.js';
import { isSymbolicSample } from './types.js';
import type { GameFile, GameSampleEntry } from '../../sampleTypes.js';

// Re-export for convenience
export type { GameFile, GameSampleEntry, GameTrackEntry } from '../../sampleTypes.js';

/**
 * Loader for game sample files with caching.
 */
export class GameSampleLoader {
  private cache = new Map<string, GameFile>();
  private pending = new Map<string, Promise<GameFile>>();
  private basePath: string;

  constructor(basePath: string = '/data/samples/games') {
    this.basePath = basePath;
  }

  /**
   * Load a game file by ID. Returns cached version if available.
   */
  async loadGame(gameId: string): Promise<GameFile> {
    // Return cached version
    if (this.cache.has(gameId)) {
      return this.cache.get(gameId)!;
    }

    // Return pending request to avoid duplicate fetches
    if (this.pending.has(gameId)) {
      return this.pending.get(gameId)!;
    }

    // Start new fetch
    const fetchPromise = this.fetchGame(gameId);
    this.pending.set(gameId, fetchPromise);

    try {
      const game = await fetchPromise;
      this.cache.set(gameId, game);
      return game;
    } finally {
      this.pending.delete(gameId);
    }
  }

  private async fetchGame(gameId: string): Promise<GameFile> {
    const response = await fetch(`${this.basePath}/${gameId}.json`);
    if (!response.ok) {
      throw new Error(`Failed to load game "${gameId}": ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Get a sample from a game by index.
   */
  async getSample(ref: SymbolicSampleRef): Promise<GameSampleEntry> {
    const game = await this.loadGame(ref.gameId);

    if (ref.index < 0 || ref.index >= game.samples.length) {
      throw new Error(
        `Sample index ${ref.index} out of range for game "${ref.gameId}" (has ${game.samples.length} samples)`
      );
    }

    return game.samples[ref.index];
  }

  /**
   * Convert loop offset (bytes) to loop start (samples).
   * BRR format: 9 bytes per block = 16 samples
   */
  static loopOffsetToSamples(loopOffset: number): number | undefined {
    if (loopOffset <= 0) return undefined;
    const loopBlock = Math.floor(loopOffset / 9);
    return loopBlock * 16;
  }

  /**
   * Resolve a symbolic sample to an inline sample.
   * Uses pre-computed baseNote from sample JSON, with user override support.
   */
  async resolveSample(sample: SymbolicSample): Promise<InlineSample> {
    const entry = await this.getSample(sample.ref);

    // Priority: user-specified > pre-computed in JSON > fallback to 60 (C4)
    const baseNote = sample.baseNote ?? entry.baseNote ?? 60;

    return {
      id: sample.id,
      brr: entry.data,
      loopStart: GameSampleLoader.loopOffsetToSamples(entry.loopOffset),
      baseNote,
      sampleRate: 32000, // SNES samples are always 32kHz
    };
  }

  /**
   * Resolve all symbolic samples in a project to inline samples.
   * Returns a new project with all samples resolved.
   */
  async resolveProject(project: Project): Promise<Project> {
    const resolvedSamples: Sample[] = await Promise.all(
      project.samples.map(async (sample) => {
        if (isSymbolicSample(sample)) {
          return this.resolveSample(sample);
        }
        return sample;
      })
    );

    return {
      ...project,
      samples: resolvedSamples,
    };
  }

  /**
   * Get all unique game IDs referenced by symbolic samples in a project.
   */
  static getReferencedGameIds(project: Project): string[] {
    const gameIds = new Set<string>();
    for (const sample of project.samples) {
      if (isSymbolicSample(sample)) {
        gameIds.add(sample.ref.gameId);
      }
    }
    return Array.from(gameIds);
  }

  /**
   * Preload all games referenced by a project.
   * Useful for reducing latency when loading a project.
   */
  async preloadGames(project: Project): Promise<void> {
    const gameIds = GameSampleLoader.getReferencedGameIds(project);
    await Promise.all(gameIds.map((id) => this.loadGame(id)));
  }

  /**
   * Clear the cache.
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Check if a game is cached.
   */
  isCached(gameId: string): boolean {
    return this.cache.has(gameId);
  }

  /**
   * Get the title of a cached game.
   */
  getGameTitle(gameId: string): string | undefined {
    return this.cache.get(gameId)?.title;
  }
}

/**
 * Default singleton instance.
 */
export const gameSampleLoader = new GameSampleLoader();
